// S9 Linear (poc CT-009, module-14 Section 2): the Linear team and project this app's tickets are handed to
// when they're assigned. "Change" picks them again from Linear (PUT /api/apps/:appId/linear). While Linear isn't
// connected nothing is handed off and there's nothing to change; people without app.manage_settings only see it.
import type { AppView } from '@feedback/shared/contracts/apps';
import type { LinearTargetInput } from '@feedback/shared/contracts/linear';
import { useState } from 'react';
import { api } from '@/lib/api';
import { fieldError, fieldErrors } from '../../lib/admin-api';
import { linearErrorText, teamLabel } from '../../lib/linear-api';
import { Button, Notice, Panel } from '../../ui/primitives';
import { appPath, useAppWrite } from '../app-data';
import {
  EMPTY_LINEAR_TARGET,
  hasLinearTargetErrors,
  type LinearTargetErrors,
  LinearTargetFields,
  linearTargetErrors,
} from '../LinearTargetFields';

const DESCRIPTION =
  'Assigning one of this app’s tickets hands it to Linear: an issue is created for the assignee in this team and project, and the ticket then follows the issue’s status.';

export function LinearSection({ app }: { app: AppView }) {
  const [editing, setEditing] = useState(false);
  const target = app.linear;
  const canChange = app.can.manageSettings && app.linearConnected;

  return (
    <Panel
      title="Linear"
      description={DESCRIPTION}
      actions={
        canChange && target && !editing ? (
          <Button size="sm" onClick={() => setEditing(true)}>
            Change
          </Button>
        ) : null
      }
    >
      {app.linearConnected ? null : (
        <Notice className="mb-3" title="Linear isn’t connected">
          Nothing is handed off: assigning a ticket moves it straight to In progress. An Admin connects the tool to
          Linear by giving it the Linear app’s client ID and secret.
        </Notice>
      )}
      <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
        <dt className="font-medium">Team</dt>
        <dd data-testid="linear-team">
          {target ? (
            teamLabel({ name: target.teamName, key: target.teamKey })
          ) : (
            <span className="text-muted">none</span>
          )}
        </dd>
        <dt className="font-medium">Project</dt>
        <dd data-testid="linear-project">{target ? target.projectName : <span className="text-muted">none</span>}</dd>
      </dl>
      {app.linearConnected && !target ? (
        <Notice tone="warning" className="mt-3" title="No Linear team and project yet">
          This app’s tickets aren’t handed off until {app.can.manageSettings ? 'you choose' : 'an Editor chooses'} a
          Linear team and project.
        </Notice>
      ) : null}
      {canChange && (editing || !target) ? (
        <LinearTargetForm
          app={app}
          onDone={() => setEditing(false)}
          onCancel={target ? () => setEditing(false) : undefined}
        />
      ) : null}
    </Panel>
  );
}

function LinearTargetForm({
  app,
  onDone,
  onCancel,
}: {
  app: AppView;
  onDone: () => void;
  /** Leave out when there's no target to go back to. */
  onCancel: (() => void) | undefined;
}) {
  const [value, setValue] = useState<LinearTargetInput>(
    app.linear ? { teamId: app.linear.teamId, projectId: app.linear.projectId } : EMPTY_LINEAR_TARGET,
  );
  const [errors, setErrors] = useState<LinearTargetErrors>({});
  const save = useAppWrite(
    app.id,
    (body: LinearTargetInput) => api.put<AppView>(appPath(app.id, '/linear'), body),
    'Linear team and project saved',
  );
  const serverFields = fieldErrors(save.error);
  const unchanged = app.linear?.teamId === value.teamId && app.linear?.projectId === value.projectId;

  return (
    <form
      className="mt-4 flex flex-col gap-4 border-t border-border pt-4"
      aria-label="Change the Linear team and project"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const found = linearTargetErrors(value);
        setErrors(found);
        if (!hasLinearTargetErrors(found)) {
          save.mutate(value, { onSuccess: onDone });
        }
      }}
    >
      <LinearTargetFields
        value={value}
        onChange={(next) => {
          setValue(next);
          setErrors({});
          save.reset();
        }}
        errors={{
          teamId: errors.teamId ?? fieldError(serverFields, 'teamId'),
          projectId: errors.projectId ?? fieldError(serverFields, 'projectId'),
        }}
      />
      {save.isError && Object.keys(serverFields).length === 0 ? (
        <Notice tone="danger">{linearErrorText(save.error)}</Notice>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" type="submit" busy={save.isPending} disabled={unchanged}>
          Save
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
